import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PauseIcon from '@mui/icons-material/Pause';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { api } from '../utils/apiService';
import CustomerInfoSection from '../components/sales-invoice/CustomerInfoSection';
import InvoiceItemsTable from '../components/sales-invoice/InvoiceItemsTable';
import InvoiceSummary from '../components/sales-invoice/InvoiceSummary';
import image1 from '../assets/riyal.png';

const controlStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: '0.875rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    transform: 'translate(14px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    }
  }
};

const multilineStyle = {
  ...controlStyle,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    minHeight: 36,
  }
};

// Calculate item totals with bidirectional discount support
const calculateItemTotals = (item, updatedField = null) => {
  const updatedItem = { ...item };
  
  updatedItem.quantity = Number(updatedItem.quantity) || 0;
  updatedItem.price = Number(updatedItem.price) || 0;
  updatedItem.discount = Number(updatedItem.discount) || 0;
  updatedItem.vatRate = Number(updatedItem.vatRate) || 0;
  updatedItem.discountAmount = Number(updatedItem.discountAmount) || 0;
  
  updatedItem.lineTotal = updatedItem.quantity * updatedItem.price;
  
  if (updatedField === 'discountAmount') {
    if (updatedItem.lineTotal > 0) {
      updatedItem.discount = (updatedItem.discountAmount * 100) / updatedItem.lineTotal;
    } else {
      updatedItem.discount = 0;
    }
  } else {
    updatedItem.discountAmount = (updatedItem.lineTotal * updatedItem.discount) / 100;
  }
  
  updatedItem.afterDiscount = updatedItem.lineTotal - updatedItem.discountAmount;
  updatedItem.vatAmount = (updatedItem.afterDiscount * updatedItem.vatRate) / 100;
  updatedItem.total = updatedItem.afterDiscount + updatedItem.vatAmount;
  updatedItem.priceWithVat = updatedItem.price * (1 + updatedItem.vatRate / 100);
  
  updatedItem.lineTotal = parseFloat(updatedItem.lineTotal.toFixed(2));
  updatedItem.discount = parseFloat(updatedItem.discount.toFixed(2));
  updatedItem.discountAmount = parseFloat(updatedItem.discountAmount.toFixed(2));
  updatedItem.afterDiscount = parseFloat(updatedItem.afterDiscount.toFixed(2));
  updatedItem.vatAmount = parseFloat(updatedItem.vatAmount.toFixed(2));
  updatedItem.total = parseFloat(updatedItem.total.toFixed(2));
  updatedItem.price = parseFloat(updatedItem.price.toFixed(2));
  updatedItem.priceWithVat = parseFloat(updatedItem.priceWithVat.toFixed(2));

  return updatedItem;
};

function QuotationForm() {
  const { quotationNo } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = Boolean(quotationNo);
  
  // Ref to track if quotation has been loaded
  const quotationLoadedRef = React.useRef(false);

  // Form state
  const [loading, setLoading] = useState(isEditMode); // Start with loading true if editing
  const [saving, setSaving] = useState(false);
  const [quotationNumber, setQuotationNumber] = useState('');
  const [quotationDate, setQuotationDate] = useState(new Date());
  const [validUntil, setValidUntil] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)); // 30 days from now
  const [status, setStatus] = useState('draft');
  const [customerType, setCustomerType] = useState('B2C');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [shouldAutoAddItem, setShouldAutoAddItem] = useState(false);
  
  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [customerData, setCustomerData] = useState({
    customerCode: 'WLKINCSTMR-119',
    customerName: 'Walk In Customer',
    customerAddress: '',
    customerPhone: '',
    customerStreetName: '',
    customerPostalCode: '',
    vat: 0,
    streetName: '',
    cityName: '',
    buildingNo: '',
    postalCode: ''
  });

  // Items state
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    product: null,
    description: '',
    quantity: 1,
    price: 0,
    vatRate: 15,
    discount: 0,
    unit: ''
  });
  const [products, setProducts] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    description: false,
    discountPerc: false,
    discountAmt: false,
    netValue: false,
    priceWithVat: true,
    vatAmount: true,
    grandTotal: true
  });
  
  // Totals state
  const [totals, setTotals] = useState({
    subtotal: 0,
    discountAmount: 0,
    additionalCost: 0,
    vat: 0,
    total: 0
  });

  // Fetch next quotation number
  const fetchNextQuotationNumber = async () => {
    try {
      const response = await api.get('/quotations/next-quotation-number');
      if (response.data.success) {
        setQuotationNumber(response.data.data.nextQuotationNumber);
        return response.data.data.nextQuotationNumber;
      }
      throw new Error('Failed to fetch quotation number');
    } catch (error) {
      console.error('Error fetching next quotation number:', error);
      enqueueSnackbar('Error fetching quotation number', { variant: 'error' });
      throw error;
    }
  };

  // Load customers, agents, and products
  useEffect(() => {
    loadCustomers();
    loadAgents();
    loadProducts();
    if (!isEditMode) {
      fetchNextQuotationNumber();
    }
  }, []);

  // Load quotation data if editing (after customers and products are loaded)
  useEffect(() => {
    if (isEditMode && customers.length > 0 && products.length > 0 && !quotationLoadedRef.current) {
      quotationLoadedRef.current = true;
      loadQuotation();
    }
  }, [isEditMode, customers.length, products.length]);

  // Auto-add item when product is selected via Enter key
  useEffect(() => {
    if (shouldAutoAddItem && newItem.product && newItem.product.id) {
      // Manually add item here to avoid dependency issues
      const itemToAdd = {
        id: Date.now(),
        product: newItem.product,
        description: newItem.description || newItem.product.description,
        quantity: newItem.quantity,
        price: newItem.price || newItem.product.price,
        vatRate: newItem.vatRate || newItem.product.vat,
        discount: newItem.discount,
        unit: newItem.unit || newItem.product.basicUnit,
        ...calculateItemTotals(newItem)
      };
      
      const updatedItems = [...items, itemToAdd];
      setItems(updatedItems);
      calculateTotals(updatedItems, totals.discountAmount, totals.additionalCost);
      
      setNewItem({
        product: null,
        description: '',
        quantity: 1,
        price: 0,
        vatRate: 15,
        discount: 0,
        unit: ''
      });
      
      setShouldAutoAddItem(false);
      
      // Focus on Qty field of the newly added row
      setTimeout(() => {
        const allQtyFields = document.querySelectorAll('[data-item-id][data-field="quantity"]');
        if (allQtyFields.length > 0) {
          const lastQtyField = allQtyFields[allQtyFields.length - 1];
          lastQtyField.focus();
        }
      }, 150);
    }
  }, [shouldAutoAddItem, newItem, items, totals.discountAmount, totals.additionalCost]);

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers');
      
      let customerList = response.data || [];
      
      // Add Walk In Customer for B2C type
      const walkInCustomer = { 
        _id: 'walk-in', 
        name: 'Walk In Customer', 
        customerType: 'B2C', 
        vatNumber: 'N/A', 
        email: 'N/A',
        id: 0
      };
      
      customerList = [
        walkInCustomer,
        ...customerList
      ];
      
      setCustomers(customerList);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await api.get('/masters/loyalty');
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await api.get('/products/active');
      if (response.data.success) {
        // Transform the product data to match the expected format
        const formattedProducts = response.data.data.map(product => ({
          id: product._id,
          name: product.productName,
          description: product.description || '',
          price: product.salesRate || 0,
          altSalesRate: product.altSalesRate || 0,
          vat: product.taxRate !== undefined && product.taxRate !== null ? product.taxRate : 15,
          productCode: product.productCode,
          basicUnit: product.basicUnit || '',
          alternateUnit: product.alternateUnit || '',
        }));
        
        setProducts(formattedProducts);
      } else {
        enqueueSnackbar('Failed to load products', { variant: 'error' });
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      enqueueSnackbar('Error loading products: ' + (error.response?.data?.message || error.message), { variant: 'error' });
      setProducts([]);
    }
  };

  const loadQuotation = async () => {
    try {
      const response = await api.get(`/quotations/${quotationNo}`);
      if (response.data && response.data.success) {
        const { master, details } = response.data.data;
        
        // Set master data
        setQuotationNumber(master.quotationNo || quotationNo);
        setQuotationDate(new Date(master.quotationDate));
        setValidUntil(new Date(master.validUntil));
        setStatus(master.status || 'draft');
        setCustomerType(master.customerType || 'B2C');
        setNotes(master.notes || '');
        setTerms(master.terms || '');
        
        // Set customer data
        setCustomerData({
          customerCode: master.customerCode,
          customerName: master.customerName,
          customerAddress: master.customerAddress || '',
          customerPhone: master.customerPhone || '',
          customerStreetName: master.customerStreetName || '',
          customerPostalCode: master.customerPostalCode || '',
          vat: master.vat || 0,
          streetName: master.streetName || '',
          cityName: master.cityName || '',
          buildingNo: master.buildingNo || '',
          postalCode: master.postalCode || ''
        });
        
        // Find and set the selected customer
        const customer = customers.find(c => c.code === master.customerCode || c.name === master.customerName);
        if (customer) {
          setSelectedCustomer(customer);
        }
        
        // Transform details to items format
        const transformedItems = details.map(detail => {
          const product = products.find(p => p.id === detail.itemId);
          return {
            id: Date.now() + Math.random(),
            product: product || {
              id: detail.itemId,
              productCode: detail.itemCode,
              name: detail.description,
              description: detail.description,
              price: detail.unitPrice,
              vat: detail.vat,
              basicUnit: detail.unit
            },
            description: detail.description,
            quantity: detail.qty,
            price: detail.unitPrice,
            vatRate: detail.vat,
            discount: detail.discountPerc || 0,
            discountAmount: detail.discountAmt || 0,
            unit: detail.unit || '',
            lineTotal: detail.qty * detail.unitPrice,
            afterDiscount: detail.netValue,
            vatAmount: detail.vatAmt,
            total: detail.grandTotal,
            priceWithVat: detail.unitPrice * (1 + detail.vat / 100)
          };
        });
        
        // Set items
        setItems(transformedItems);
        
        // Set totals
        setTotals({
          subtotal: master.subTotal || 0,
          discountAmount: master.discountAmt || 0,
          additionalCost: master.additionalCost || 0,
          vat: master.vatAmt || 0,
          total: master.totalAmt || 0
        });
      }
    } catch (error) {
      console.error('Error loading quotation:', error);
      enqueueSnackbar('Failed to load quotation', { variant: 'error' });
      navigate('/quotation');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const calculateTotals = useCallback((itemsList, discountAmt = 0, additionalCost = 0) => {
    const subtotal = itemsList.reduce((sum, item) => {
      const itemSubtotal = (item.quantity || 0) * (item.price || 0);
      return sum + itemSubtotal;
    }, 0);
    
    const vat = itemsList.reduce((sum, item) => sum + (item.vatAmount || 0), 0);
    const total = subtotal + vat + additionalCost - discountAmt;
    
    setTotals({
      subtotal: subtotal,
      discountAmount: discountAmt,
      additionalCost: additionalCost,
      vat: vat,
      total: total
    });
  }, []);

  // Handle new item change
  const handleNewItemChange = (field, value) => {
    const updatedNewItem = {
      ...newItem,
      [field]: value
    };
    
    if (field === 'unit' && newItem.product) {
      if (value === newItem.product.basicUnit) {
        updatedNewItem.price = newItem.product.price;
      } else if (value === newItem.product.alternateUnit) {
        updatedNewItem.price = newItem.product.altSalesRate;
      }
    }
    
    const calculatedItem = calculateItemTotals(updatedNewItem, field);
    setNewItem(calculatedItem);
  };

  // Handle product change
  const handleProductChange = (event, newValue, shouldAutoAdd = false) => {
    if (newValue) {
      const itemWithProduct = {
        product: newValue,
        description: newValue.description || '',
        quantity: 1,
        price: newValue.price || 0,
        vatRate: newValue.vat || 15,
        discount: 0,
        unit: newValue.basicUnit || '',
      };
      
      const calculatedItem = calculateItemTotals(itemWithProduct);
      setNewItem({
        ...calculatedItem,
        product: newValue
      });

      // If auto-add is requested (Enter key pressed), set flag for useEffect to handle
      if (shouldAutoAdd) {
        setShouldAutoAddItem(true);
      }
    } else {
      setNewItem({
        product: null,
        description: '',
        quantity: 1,
        price: 0,
        vatRate: 15,
        discount: 0,
        unit: '',
      });
    }
  };

  // Handle add item
  const handleAddItem = useCallback(() => {
    if (!newItem.product || !newItem.product.id) {
      return;
    }
    
    const itemToAdd = {
      id: Date.now(),
      product: newItem.product,
      description: newItem.description || newItem.product.description,
      quantity: newItem.quantity,
      price: newItem.price || newItem.product.price,
      vatRate: newItem.vatRate || newItem.product.vat,
      discount: newItem.discount,
      unit: newItem.unit || newItem.product.basicUnit,
      ...calculateItemTotals(newItem)
    };
    
    const updatedItems = [...items, itemToAdd];
    setItems(updatedItems);
    calculateTotals(updatedItems, totals.discountAmount, totals.additionalCost);
    
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
      unit: ''
    });
  }, [newItem, items, totals.discountAmount, totals.additionalCost]);

  // Handle remove item
  const handleRemoveItem = (itemId) => {
    const updatedItems = items.filter(item => item.id !== itemId);
    setItems(updatedItems);
    calculateTotals(updatedItems, totals.discountAmount, totals.additionalCost);
  };

  // Handle item change
  const handleItemChange = (itemId, field, value) => {
    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        let updatedItem = { ...item };
        
        if (field === 'unit') {
          if (value === item.product.basicUnit) {
            updatedItem.price = item.product.price;
          } else if (value === item.product.alternateUnit) {
            updatedItem.price = item.product.altSalesRate;
          }
        }
        
        updatedItem[field] = value;
        const calculatedItem = calculateItemTotals(updatedItem, field);
        return calculatedItem;
      }
      return item;
    });

    setItems(updatedItems);
    calculateTotals(updatedItems, totals.discountAmount, totals.additionalCost);
  };

  // Handle customer change
  const handleCustomerChange = useCallback((event, customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      // Set default customerCode for Walk In Customer
      const customerCode = customer.code || (customer._id === 'walk-in' || customer.name === 'Walk In Customer' ? 'WALK-IN' : '');
      
      setCustomerData({
        customerCode: customerCode,
        customerName: customer.name || '',
        customerAddress: customer.address?.street || '',
        customerPhone: customer.phone || '',
        customerStreetName: customer.address?.streetName || '',
        customerPostalCode: customer.address?.postalCode || '',
        vat: customer.vatRegistrationNumber ? 15 : 0,
        streetName: customer.address?.streetName || '',
        cityName: customer.address?.city || '',
        buildingNo: customer.address?.buildingNo || '',
        postalCode: customer.address?.postalCode || ''
      });
    }
  }, []);

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!customerData.customerName) {
      enqueueSnackbar('Please select a customer', { variant: 'error' });
      return;
    }

    if (items.length === 0) {
      enqueueSnackbar('Please add at least one item', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Fetch latest quotation number before saving (for new quotations only)
      let currentQuotationNumber = quotationNumber;
      if (!isEditMode) {
        currentQuotationNumber = await fetchNextQuotationNumber();
      }

      // Transform items to match backend expected format
      const transformedItems = items.map(item => ({
        itemId: item.product.id,
        itemCode: item.product.productCode || '',
        description: item.description || item.product.description || item.product.name,
        qty: item.quantity,
        unitPrice: item.price,
        vat: item.vatRate,
        discountPerc: item.discount,
        discountAmt: item.discountAmount,
        netValue: item.afterDiscount,
        vatAmt: item.vatAmount,
        grandTotal: item.total,
        unit: item.unit
      }));

      const quotationData = {
        master: {
          quotationDate: quotationDate.toISOString(),
          validUntil: validUntil.toISOString(),
          status: status,
          customerType: customerType,
          ...customerData,
          // Ensure customerCode is set for Walk In Customer
          customerCode: customerData.customerCode || 'WALK-IN',
          notes: notes,
          terms: terms,
          totalAmt: {
            subtotal: totals.subtotal,
            vat: totals.vat,
            additionalCost: totals.additionalCost,
            discountAmount: totals.discountAmount
          }
        },
        details: transformedItems
      };

      if (isEditMode) {
        await api.put(`/quotations/${quotationNo}`, quotationData);
        enqueueSnackbar('Quotation updated successfully', { variant: 'success' });
      } else {
        await api.post('/quotations', quotationData);
        enqueueSnackbar('Quotation created successfully', { variant: 'success' });
      }

      navigate('/quotation');
    } catch (error) {
      console.error('Error saving quotation:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to save quotation', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={() => navigate('/quotation')}
                sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
              >
                <CloseIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                  {isEditMode ? 'Edit Quotation' : 'New Quotation'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                  {isEditMode ? 'Update quotation details' : 'Create a new quotation'}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/quotation')}
                sx={{ fontSize: '0.875rem' }}
              >
                Cancel
              </Button>
            </Box>
          </Box>

          {/* Info Alert */}
          <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
            Create and manage quotations for your customers. All fields marked with * are required.
          </Alert>

          {/* Main Form Sections */}
          <Grid container spacing={0.5}>
            {/* Quotation Details */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 0.5, 
                  mb: 0.5, 
                  borderRadius: '4px',
                  boxShadow: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                  Quotation Details
                </Typography>
                <Grid container spacing={0.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Quotation Number"
                      value={quotationNumber}
                      onChange={(e) => setQuotationNumber(e.target.value)}
                      fullWidth
                      margin="dense"
                      size="small"
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                      <Select
                        value={status}
                        label="Status"
                        onChange={(e) => setStatus(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="draft" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#4361ee', fontWeight: 500 }}>Draft</Box>
                        </MenuItem>
                        <MenuItem value="approved" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#2ecd6f', fontWeight: 500 }}>Approved</Box>
                        </MenuItem>
                        <MenuItem value="po" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#9c27b0', fontWeight: 500 }}>PO</Box>
                        </MenuItem>
                        <MenuItem value="rejected" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#f44336', fontWeight: 500 }}>Rejected</Box>
                        </MenuItem>
                        <MenuItem value="sent" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#2196f3', fontWeight: 500 }}>Sent</Box>
                        </MenuItem>
                        <MenuItem value="partial" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#ff9800', fontWeight: 500 }}>Partial</Box>
                        </MenuItem>
                        <MenuItem value="expired" sx={{ fontSize: '0.875rem' }}>
                          <Box sx={{ color: '#9e9e9e', fontWeight: 500 }}>Expired</Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Quotation Date"
                      value={quotationDate}
                      onChange={(newValue) => setQuotationDate(newValue)}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: 'dense',
                          size: 'small',
                          sx: controlStyle
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Valid Until"
                      value={validUntil}
                      onChange={(newValue) => setValidUntil(newValue)}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: 'dense',
                          size: 'small',
                          sx: controlStyle
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Customer Info */}
            <Grid item xs={12} md={6}>
              <CustomerInfoSection
                customerType={customerType}
                customer={selectedCustomer}
                agent={selectedAgent}
                customers={customers}
                agents={agents}
                onCustomerTypeChange={(e) => setCustomerType(e.target.value)}
                onCustomerChange={handleCustomerChange}
                onAgentChange={(agent) => setSelectedAgent(agent)}
                onOpenAgentDialog={() => {}}
                hideLoyalty={true}
              />
            </Grid>

            {/* Items Table */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Quotation Items
              </Typography>
              <InvoiceItemsTable
                items={items}
                newItem={newItem}
                visibleColumns={visibleColumns}
                products={products}
                loading={loading}
                onItemChange={handleItemChange}
                onRemoveItem={handleRemoveItem}
                onAddItem={handleAddItem}
                onProductChange={handleProductChange}
                onNewItemChange={handleNewItemChange}
                onKeyDown={() => {}}
                onNewItemKeyDown={() => {}}
                onFocus={() => {}}
                onSettingsClick={() => {}}
                onColumnToggle={(column) => {
                  setVisibleColumns({ ...visibleColumns, [column]: !visibleColumns[column] });
                }}
                productInputRef={null}
              />
            </Grid>

          </Grid>

          {/* Summary and Notes Section */}
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes or comments..."
                        variant="outlined"
                        size="small"
                        sx={{
                          ...multilineStyle,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            width: '100%'
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Terms & Conditions"
                        value={terms}
                        onChange={(e) => setTerms(e.target.value)}
                        placeholder="Add terms and conditions..."
                        variant="outlined"
                        size="small"
                        sx={{
                          ...multilineStyle,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            width: '100%'
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Summary */}
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Quotation Summary
                  </Typography>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      borderRadius: '4px', 
                      bgcolor: '#ffffff', 
                      border: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    {/* Summary Section */}
                    <Box sx={{ mb: 1.5 }}>
                      {/* Subtotal */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Typography variant="body1">Subtotal</Typography>
                        <Typography variant="body1" sx={{ mr: 1 }}>{totals.subtotal.toFixed(2)}</Typography>
                      </Box>

                      {/* Discount */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Typography variant="body1">Discount</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            value={totals.discountAmount === 0 ? '' : totals.discountAmount}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setTotals({ ...totals, discountAmount: value });
                              calculateTotals(items, value, totals.additionalCost);
                            }}
                            type="number"
                            size="small"
                            InputProps={{
                              inputProps: { 
                                min: 0, 
                                step: 0.01,
                                style: { textAlign: 'right', padding: '6px 8px' }
                              }
                            }}
                            sx={{ 
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                height: '32px'
                              },
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                display: 'none',
                                margin: 0
                              },
                              '& input[type=number]': {
                                MozAppearance: 'textfield'
                              }
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Additional Cost */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Typography variant="body1">Additional Cost</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            value={totals.additionalCost === 0 ? '' : totals.additionalCost}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setTotals({ ...totals, additionalCost: value });
                              calculateTotals(items, totals.discountAmount, value);
                            }}
                            type="number"
                            size="small"
                            InputProps={{
                              inputProps: { 
                                min: 0, 
                                step: 0.01,
                                style: { textAlign: 'right', padding: '6px 8px' }
                              }
                            }}
                            sx={{ 
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                height: '32px'
                              },
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                display: 'none',
                                margin: 0
                              },
                              '& input[type=number]': {
                                MozAppearance: 'textfield'
                              }
                            }}
                          />
                        </Box>
                      </Box>

                      {/* VAT */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Typography variant="body1">VAT</Typography>
                        <Typography variant="body1" sx={{ mr: 1 }}>{totals.vat.toFixed(2)}</Typography>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Total */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography variant="h6" fontWeight={600}>Total</Typography>
                        <Typography variant="h6" fontWeight={600} sx={{ mr: 1, display: 'flex', alignItems: 'center' }}><img src={image1} alt="SAR" style={{ width: '16px', height: '16px', marginRight: '4px' }} />{totals.total.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                    
                    {/* Action Buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                      pt: 1.5
                    }}>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        size="small"
                        sx={{ borderRadius: 0 }}
                        disabled={loading}
                      >
                        Print
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                        onClick={handleSave}
                        disabled={saving || items.length === 0}
                        size="small"
                        sx={{ borderRadius: 0 }}
                      >
                        {saving ? 'Saving...' : 'Save Quotation'}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
}

export default QuotationForm;
